import React, { Component, Fragment } from 'react';
import { parse } from 'querystring';
import PropTypes from 'prop-types';
import axios from 'axios';
import cx from 'classnames';

// @material-ui/icons
import Work from '@material-ui/icons/Work';
import Domain from '@material-ui/icons/Domain';
import AddAlert from '@material-ui/icons/AddAlert';
import Check from '@material-ui/icons/Check';
import Email from '@material-ui/icons/Email';
import LockOutlined from '@material-ui/icons/LockOutlined';

// material-ui components
import withStyles from '@material-ui/core/styles/withStyles';
import Checkbox from '@material-ui/core/Checkbox';

// core components
import ErrorBoundaries from 'components/Wrapper/ErrorBoundaries';
import Button from 'components/CustomButtons/Button';
import GridContainer from 'components/Grid/GridContainer';
import ItemGrid from 'components/Grid/ItemGrid';
import IconCard from 'components/Cards/IconCard';
import CustomInput from 'components/CustomInput/CustomInput';
import CustomPasswordInput from '../../components/CustomInput/CustomPasswordInput';
import CustomSelectWithId from 'components/CustomInput/CustomSelectWithId';
import CustomAutoComplete from 'components/CustomInput/CustomAutoComplete';
import SearchBar from 'components/SearchBar/SearchBar';
import ImageUpload from 'components/CustomUpload/ImageUpload';
import Snackbar from 'components/Snackbar/Snackbar';

// others
import config from 'config';
import FormHOC from 'hoc/FormHOC';
import { verifyEmail, transformPhone } from 'helpers/helpers';

// style / context
import { Context } from 'context/Context';
import detailsPageStyle from 'views/Common/style/detailsPageStyle';
import { SECTORS } from 'data';
import { FormControlLabel, Typography } from '@material-ui/core';
import { PRO_IMAGE_RIGHTS_CHECKBOX } from '../../data';

class ProHrRegister extends Component {
  state = {
    userType: '',
    user: null,
    companies: [],
    disabledForHr: false,
    canChangeCompany: true,
    imageRightsAgreement: false,
  };

  handleImageRightsAgreementChange = (event) => {
    this.setState({ imageRightsAgreement: event.target.checked });
  };

  componentDidMount() {
    const { location } = this.props;
    const userType = location.pathname === '/auth/professionnel' ? 'pro' : 'hr';
    const search = parse(this.props.location.search, '&', '=');
    const userToken = search['?token'];
    const userEmail = search['?email'] && encodeURI(search['?email']).replace('%20', '+');
    const userCompanyId = search['companyId'];
    const userCompanyName = search['companyName'];

    this.setState((prevState) => ({
      ...prevState,
      userType,
      canChangeCompany: userType !== 'hr',
    }));

    this.verify(
      {
        token: userToken,
        email: userEmail,
        company: {
          _id: userCompanyId,
          name: userCompanyName,
        },
      },
      userType,
    );
    this.state.canChangeCompany && this.fetchCompanies();
  }

  componentWillUnmount() {
    this.props.context.closeSnackbar();
  }

  verify = (datas, userType) => {
    if (datas.email || datas.token) {
      const type = datas.token ? 'token' : 'email';
      axios
        .post(`${config.API_URL}/public/auth/verify_${type}`, {
          [type]: datas[type],
        })
        .then(({ data: { user } }) => {
          if (user.account.type === userType) {
            if (user.account.is_active) {
              this.props.context.updateUser(user);
              localStorage.setItem('token', JSON.stringify(user.token));
              this.props.history.push('/');
            } else {
              this.handle_auto_email_and_company(user.email, user.account.company);
              this.setState((prevState) => ({ ...prevState, user }));
            }
          }
        })
        .catch(() => {
          if (userType === 'hr') {
            this.setState((prevState) => ({
              ...prevState,
              disabledForHr: true,
            }));
          } else if (userType === 'pro') {
            this.handle_auto_email_and_company(datas.email, datas.company);
          }
        });
    }
  };

  handle_auto_email_and_company = (email, company) => {
    const { handleOnChange, handleOnSelect } = this.props;
    handleOnChange(email, 'email', 'email');
    handleOnChange(company._id, 'company', 'autocomplete');
    handleOnSelect(company.name, company._id, 'company');
  };

  signUp = async () => {
    const {
      email,
      password,
      firstName: first_name,
      lastName: last_name,
      address,
      longitude,
      latitude,
      department,
      handleOnSubmit,
      handleSignUp,
      companyAutoComplete,
      context,
    } = this.props;
    const newCompany = companyAutoComplete === 'Nouvelle Entreprise';
    const phone = transformPhone(this.props.phone);
    const isPro = this.props.location.pathname === '/auth/professionnel' && true;
    const validations = ['firstName', 'lastName', 'email', 'password', 'confirmPassword', 'checkboxCgu'];
    if (phone) validations.push('phone');
    if (isPro) validations.push('address');
    if (newCompany) validations.push('name', 'industry');
    else validations.push('company');

    // Cas AUCUNE donnée venant des étapes antérieures
    //if (!this.props.location.state) return this.props.history.push('/auth')

    // Cas données INCOMPLÈTES venant des étapes antérieures
    const type = this.state.userType;

    const { access_token } = this.state;

    if (!handleOnSubmit(validations)) return null;

    const company = newCompany ? await this.createCompany() : this.props.company;
    if (!company) return null;

    let body = {
      access_token,
      email,
      password,
      type,
      first_name,
      last_name,
      ...(isPro && { address: address }),
      ...(isPro && { loc: [longitude, latitude] }),
      ...(isPro && { department: department }),
      ...(isPro && { image_rights_agreement: this.state.imageRightsAgreement }),
      ...(isPro && { image_rights_agreement_date: new Date() }),
      ...(phone && { phone }),
      company,
    };

    if (this.state.user) body = { ...body, id: this.state.user._id };

    handleSignUp('', body, context);
  };

  checkEmail = async (email) => {
    return await axios
      .post(`${config.API_URL}/public/auth/verify_email`, { email })
      .then((res) => ({
        valid: res.data.user && this.state.userType === res.data.user.account.type,
        user: res.data.user ? res.data.user : {},
      }))
      .catch(() => ({ valid: false, user: {} }));
  };

  // function related to company
  createCompany = async () => {
    const { name, industry, logo, handleOnSubmit } = this.props;
    const body = { name, industry, ...(logo && { logo }) };

    if (!handleOnSubmit(['name', 'industry'])) return null;

    return await axios
      .post(`${config.API_URL}/public/companies/add`, body)
      .then((res) => {
        // Refresh company list and update company field to select the new created one
        this.fetchCompanies().then(() => {
          this.props.handleOnChange(res.data._id, 'company', 'autocomplete');
          this.props.handleOnSelect(res.data.name, res.data._id, 'company');
        });
        return res.data._id;
      })
      .catch((e) => {
        const message =
          e.response && e.response.data.error.code === 11000
            ? "Une entreprise avec ce nom existe déjà. Cliquez sur 'Entreprise déjà inscrite' pour la rejoindre"
            : "Un problème a eu lieu lors de la création de l'entreprise.";

        this.props.context.handleSnackbar({
          status: 'danger',
          message,
        });
      });
  };

  fetchCompanies = async () => {
    return axios
      .get(`${config.API_URL}/public/companies/all`)
      .then((response) => {
        this.setState({
          companies: response.data,
        });
      })
      .catch(() => {
        this.props.context.handleSnackbar({
          status: 'danger',
          message: 'Problème serveur. Veuillez recharger la page.',
        });
      });
  };

  hrChangeCompany = (value) => {
    const { handleOnChange, handleOnSelect, clearInput, unValidate } = this.props;
    const { userType } = this.state;
    if (userType === 'hr') {
      if (verifyEmail(value)) {
        this.checkEmail(value).then(({ valid, user }) => {
          this.setState((prevState) => ({
            ...prevState,
            disabledForHr: !valid,
            user,
          }));
          if (valid) {
            handleOnChange(user.account.company._id, 'company', 'autocomplete');
            handleOnSelect(user.account.company.name, user.account.company._id, 'company');
          } else {
            unValidate(['email']);
            clearInput(['company', 'companyAutoComplete']);
          }
        });
      } else {
        clearInput(['company', 'companyAutoComplete']);
      }
    }
  };

  render() {
    const { userType, companies, canChangeCompany, disabledForHr } = this.state;
    const {
      classes,
      email,
      emailState,
      passwordState,
      confirmPasswordState,
      checkboxCguState,
      checkboxCgu,
      firstName,
      firstNameState,
      lastName,
      lastNameState,
      addressState,
      phoneState,
      checkedState,
      companyAutoComplete,
      companyState,
      industry,
      industryState,
      nameState,
      logoLoading,
      logoState,
      // function
      handleOnChange,
      handleOnSelect,
      handleChangeAddress,
      handleSelectAddress,
      handleChangeImage,
      handleRemoveImage,
      clearInput,
    } = this.props;
    const {
      closeSnackbar,
      state: { snackbar },
    } = this.props.context;

    const ImageUploadClass = cx({
      [classes.borderButton]: true,
      [classes.addLogo]: true,
      [classes.noBorderButton]: logoLoading,
    });

    const selectCompanyButtonsClass = cx({
      [classes.selectCompanyButtons]: true,
      [classes.selectCompanyButtonsError]: checkedState === 'error',
    });

    let valSelect = industry;
    if (!Array.isArray(valSelect)) {
      valSelect = [];
    }
    valSelect.forEach((item) => {
      if (SECTORS.indexOf(item) === -1) {
        valSelect.splice(item);
      }
    });

    const isPro = this.props.location.pathname === '/auth/professionnel' && true;
    const title = `Formulaire ${isPro ? 'Tuteur' : 'Ressources humaines'}`;
    const iconColor = isPro ? 'purple' : 'yellow';
    const icon = isPro ? Work : Domain;

    const selectACompany = (
      <CustomAutoComplete
        success={companyState === 'success'}
        error={companyState === 'error'}
        labelText={
          <span>
            Entreprise <small>(obligatoire)</small>
          </span>
        }
        {...(userType === 'pro' && {
          helpText:
            "Taper les premières lettres de votre entreprise. Si vous ne trouvez pas votre entreprise, cliquez sur 'Nouvelle entreprise' dans la liste déroulante.",
        })}
        value={companyAutoComplete || ''}
        data={companies}
        maxResults={'100'}
        additionalValues={canChangeCompany ? [{ _id: 'new-company', name: 'Nouvelle Entreprise' }] : []}
        handleChange={(event) => canChangeCompany && handleOnChange(event.target.value, 'company', 'autocomplete')}
        handleSelect={(name, id) => canChangeCompany && handleOnSelect(name, id, 'company')}
      />
    );

    const addACompany = (
      <Fragment>
        <div className={classes.selectCompanyButtonsWrapper}>
          <Checkbox
            tabIndex={-1}
            onClick={() => clearInput(['company', 'companyAutoComplete', 'name', 'industry', 'logo'])}
            checked={companyAutoComplete === 'Nouvelle Entreprise'}
            checkedIcon={<Check className={classes.checkedIcon} />}
            classes={{
              checked: classes.checked,
            }}
          />
          <div className={selectCompanyButtonsClass}>Nouvelle Entreprise</div>
        </div>
        <CustomInput
          key='input-company'
          success={nameState === 'success'}
          error={nameState === 'error'}
          labelText={
            <span>
              Entreprise <small>(obligatoire)</small>
            </span>
          }
          id='company'
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            onChange: (e) => handleOnChange(e.target.value, 'name', 'length', 2),
          }}
        />
        <CustomSelectWithId
          key='input-industry'
          noIds
          multiple={true}
          success={industryState === 'success'}
          error={industryState === 'error'}
          labelText={
            <span>
              Secteur <small>(obligatoire)</small>
            </span>
          }
          value={valSelect ? valSelect : ''}
          data={SECTORS}
          handleChange={(event) => handleOnChange(event.target.value, 'industry', 'multiSelect')}
        />
        <div key='input-logo' className={classes.logoContainer}>
          {this.props.logo && <img alt='logo-company' className={classes.logoImg} src={this.props.logo} />}
          <ImageUpload
            success={logoState === 'success'}
            color='white'
            customClass={ImageUploadClass}
            labelText='Ajouter un logo'
            loading={logoLoading}
            handleChange={(event) => handleChangeImage(event, 'logo')}
            handleRemove={() => handleRemoveImage('logo')}
          />
        </div>
      </Fragment>
    );

    const content = (
      <GridContainer className={classes.topMargin}>
        <ItemGrid xs={12} sm={6}>
          <CustomInput
            success={emailState === 'success'}
            error={emailState === 'error'}
            helpText={
              emailState === 'error' &&
              userType === 'hr' &&
              disabledForHr && (
                <span className={classes.danger}>
                  Votre mail ne correspond pas à un mail autorisé à créer un compte RH. Cette fonctionnalité est réservé
                  à nos partenaires premium. Pour plus d’informations contactez nous à&nbsp;
                  <a href='mailto:contact@viensvoirmontaf.fr'>contact@viensvoirmontaf.fr</a>
                </span>
              )
            }
            labelText={
              <span>
                <Email className={classes.inputLabelIcon} /> Email <small>(obligatoire)</small>
              </span>
            }
            id='email'
            value={email || ''}
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              type: 'email',
              onChange: async (event) => {
                handleOnChange(event.target.value, 'email', 'email');
                this.hrChangeCompany(event.target.value);
              },
            }}
            required
          />
          <CustomPasswordInput
            success={passwordState === 'success'}
            error={passwordState === 'error'}
            labelText={
              <span>
                <LockOutlined className={classes.inputLabelIcon} /> Mot de passe <small>(obligatoire)</small>
              </span>
            }
            id='password'
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              type: 'password',
              onChange: (event) => handleOnChange(event.target.value, 'password', 'password'),
            }}
            required
          />
          <CustomPasswordInput
            success={confirmPasswordState === 'success'}
            error={confirmPasswordState === 'error'}
            labelText={
              <span>
                <LockOutlined className={classes.inputLabelIcon} /> Confirmez le mot de passe{' '}
                <small>(obligatoire)</small>
              </span>
            }
            id='confirmPassword'
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              type: 'password',
              onChange: (event) => handleOnChange(event.target.value, 'confirmPassword', 'equalTo', 'password'),
            }}
            required
          />
        </ItemGrid>
        <ItemGrid xs={12} sm={6}>
          <CustomInput
            success={firstNameState === 'success'}
            error={firstNameState === 'error'}
            labelText={
              <span>
                Prénom <small>(obligatoire)</small>
              </span>
            }
            id='first-name'
            value={firstName || ''}
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              onChange: (event) => handleOnChange(event.target.value, 'firstName', 'length', 3),
            }}
            required
          />
          <CustomInput
            success={lastNameState === 'success'}
            error={lastNameState === 'error'}
            labelText={
              <span>
                Nom de famille <small>(obligatoire)</small>
              </span>
            }
            id='last-name'
            value={lastName || ''}
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              onChange: (event) => handleOnChange(event.target.value, 'lastName', 'length', 3),
            }}
            required
          />
          <CustomInput
            success={phoneState === 'success'}
            error={phoneState === 'error'}
            labelText='Téléphone portable'
            {...(!isPro && {
              helpText: 'Vos coordonnées resteront confidentielles et ne seront pas transmises aux étudiants.',
            })}
            id='phone'
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              onChange: (event) => handleOnChange(event.target.value, 'phone', 'optionnalLandline'),
            }}
          />
        </ItemGrid>
        {isPro && (
          <ItemGrid xs={12} sm={6}>
            <SearchBar
              success={addressState === 'success'}
              error={addressState === 'error'}
              type='address'
              multipleCountry={true}
              labelText={
                <span>
                  Lieu du stage <small>(obligatoire)</small>
                </span>
              }
              helpText='Vos coordonnées resteront confidentielles et ne seront pas transmises aux étudiants.'
              handleChange={handleChangeAddress}
              handleSelect={handleSelectAddress}
            />
          </ItemGrid>
        )}
        <ItemGrid xs={12} sm={isPro ? 6 : 12}>
          {companyAutoComplete === 'Nouvelle Entreprise' ? addACompany : selectACompany}
        </ItemGrid>
      </GridContainer>
    );

    // noinspection DuplicatedCode
    const footer = (
      <Fragment>
        <GridContainer direction='row' justifyContent='center' alignItems='center'>
          <ItemGrid>
            <FormControlLabel
              required
              control={
                <Checkbox
                  onClick={() => handleOnChange(!checkboxCgu, 'checkboxCgu', 'checked')}
                  checked={checkboxCgu}
                  className={checkboxCguState === 'error' ? classes.checkboxError : ''}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              }
              label={
                <Typography style={{ fontSize: '14px' }}>
                  J'ai lu et j'accepte les{' '}
                  <a href='https://www.viensvoirmontaf.fr/mentions-legales/' rel='noopener noreferrer' target='_blank'>
                    conditions générales d'utilisation de ViensVoirMonTaf
                  </a>{' '}
                  et j'atteste que les informations renseignées sont vraies.
                </Typography>
              }
            />
          </ItemGrid>
        </GridContainer>

        {isPro && (
          <GridContainer direction='row' justifyContent='center' alignItems='center'>
            <ItemGrid>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={this.state.imageRightsAgreement}
                    onChange={this.handleImageRightsAgreementChange}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
                label={<Typography style={{ fontSize: '14px' }}>{PRO_IMAGE_RIGHTS_CHECKBOX}</Typography>}
              />
            </ItemGrid>
          </GridContainer>
        )}

        <Button color='primary' onClick={() => this.signUp(this.props.context)}>
          Inscription
        </Button>
      </Fragment>
    );

    const snackbarContainer = snackbar && snackbar.isSnackbar && (
      <Snackbar
        key='snackbar'
        place='bc'
        color={snackbar.status}
        icon={AddAlert}
        message={snackbar.message}
        open={true}
        closeNotification={closeSnackbar}
        close
      />
    );

    return (
      <ErrorBoundaries>
        <div className={classes.content}>
          {snackbarContainer}
          <GridContainer justifyContent='center'>
            <ItemGrid xs={12} sm={10}>
              <form>
                <IconCard
                  icon={icon}
                  iconColor={iconColor}
                  title={title}
                  content={content}
                  footer={footer}
                  footerAlign='center'
                />
              </form>
            </ItemGrid>
          </GridContainer>
        </div>
      </ErrorBoundaries>
    );
  }
}

ProHrRegister.propTypes = {
  classes: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  handleOnChange: PropTypes.func.isRequired,
  handleOnSubmit: PropTypes.func.isRequired,
  handleChangeAddress: PropTypes.func.isRequired,
  handleSelectAddress: PropTypes.func.isRequired,
  handleChangeImage: PropTypes.func.isRequired,
  handleRemoveImage: PropTypes.func.isRequired,
  unValidate: PropTypes.func.isRequired,
  firstName: PropTypes.string,
  firstNameState: PropTypes.string,
  lastName: PropTypes.string,
  lastNameState: PropTypes.string,
  phone: PropTypes.string,
  phoneState: PropTypes.string,
  address: PropTypes.string,
  addressState: PropTypes.string,
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  department: PropTypes.string,
  company: PropTypes.string,
  companyState: PropTypes.string,
  industry: PropTypes.string,
  industryState: PropTypes.string,
  logo: PropTypes.string,
  logoLoading: PropTypes.bool,
  logoState: PropTypes.string,
  imageRightsAgreement: PropTypes.bool,
  imageRightsAgreementDate: PropTypes.instanceOf(Date),
};

const WrappedComponent = FormHOC(ProHrRegister);

export default withStyles(detailsPageStyle)((props) => (
  <Context.Consumer>{(context) => <WrappedComponent {...props} context={context} />}</Context.Consumer>
));
